/**
 * Enterprise, as the organization pages draw it — for every feature gated on it.
 *
 * Two pieces, and the later lots (single sign-on, SCIM, enforced two-factor,
 * verified domains, audit streaming) use both:
 *
 * - `EnterpriseBadge` — the one-word chip beside a heading or a setting that
 *   only Enterprise has. Same chip everywhere, so "Enterprise" means one thing.
 * - `EnterpriseOnly` — wraps such a feature's controls. On Enterprise it draws
 *   them; otherwise it draws a short notice in their place naming what is
 *   missing and who can change it, rather than controls the Worker would answer
 *   with a 402 (`requireEnterprise` in `tier.ts`). The page decides what to
 *   draw; the Worker decides what is allowed, and both read the same fact
 *   (`isEnterpriseOrg` here, `isEnterprise` there).
 *
 *     <EnterpriseOnly organization={organization} feature="Single sign-on">
 *       <SsoSettings … />
 *     </EnterpriseOnly>
 */
import type React from 'react';
import { color, font, radius } from '../theme/tokens';
import { isEnterpriseOrg } from '../lib/billing';
import type { Organization } from '../lib/types';

export function EnterpriseBadge(): React.JSX.Element {
  return (
    <span
      style={{
        fontFamily: font.mono,
        fontSize: 11.5,
        color: color.accentText,
        background: color.noteTealBg,
        border: `1px solid ${color.accent}`,
        padding: '4px 8px',
        borderRadius: radius.chip,
        whiteSpace: 'nowrap'
      }}
    >
      Enterprise
    </span>
  );
}

export function EnterpriseOnly({
  organization,
  feature,
  children
}: {
  organization: Organization | null;
  /** What the feature is called, for the notice: "Single sign-on". */
  feature: string;
  children: React.ReactNode;
}): React.JSX.Element {
  if (isEnterpriseOrg(organization)) return <>{children}</>;
  const owner = organization?.role === 'owner';
  return (
    <div
      style={{
        border: `1px dashed ${color.lineInput}`,
        borderRadius: radius.md,
        padding: '16px 18px',
        display: 'flex',
        gap: 12,
        alignItems: 'flex-start'
      }}
    >
      <EnterpriseBadge />
      <div style={{ fontSize: 13.5, color: color.inkSubtle, lineHeight: 1.6 }}>
        {feature} is part of Enterprise.{' '}
        {owner
          ? 'You can move this organization to Enterprise on Billing.'
          : 'The owner of this organization can move it to Enterprise on Billing.'}
      </div>
    </div>
  );
}
